const DB_NAME = 'kn_hts_metadata_cache';
const STORE_NAME = 'hts_metadata';
const DB_VERSION = 1;
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cache the Promise (not the resolved db instance) so concurrent callers
// all await the same open request instead of spawning multiple open requests.
let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => {
      console.error('[htsIDBCache] Failed to open IndexedDB:', req.error);
      _dbPromise = null; // allow retry on next call
      reject(req.error);
    };
  });
  return _dbPromise;
}

/**
 * Build a stable, session-independent cache key.
 * The window cache uses a 3-hour time bucket; IDB uses the actual date string
 * so entries persist and are reused across page loads for the same tariff date.
 */
export function buildIDBCacheKey(hts, countryOfOrigin, entryDateMMDDYYYY) {
  return `hts_${hts}_${countryOfOrigin}_${entryDateMMDDYYYY || 'nodate'}`;
}

/**
 * Read a cached HTS metadata response from IndexedDB.
 * Returns null if missing or expired. Never throws.
 */
export async function idbGetHtsMetadata(cacheKey) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .get(cacheKey);
      req.onsuccess = () => {
        const record = req.result;
        if (!record || Date.now() > record.expiresAt) return resolve(null);
        resolve(record.data);
      };
      req.onerror = () => {
        console.error('[htsIDBCache] Read error for key:', cacheKey, req.error);
        resolve(null);
      };
    });
  } catch (e) {
    console.error('[htsIDBCache] idbGetHtsMetadata failed:', e);
    return null;
  }
}

/**
 * Write an HTS metadata response to IndexedDB with a 24-hour TTL.
 * Skips write if data is empty/null. Never throws — writes are best-effort.
 */
export async function idbSetHtsMetadata(cacheKey, data) {
  if (!data) return;
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({ cacheKey, data, expiresAt: Date.now() + TTL_MS });
      tx.oncomplete = resolve;
      tx.onerror = () => {
        console.error('[htsIDBCache] Write error for key:', cacheKey, tx.error);
        reject(tx.error);
      };
    });
  } catch (e) {
    console.error('[htsIDBCache] idbSetHtsMetadata failed:', e);
  }
}

/**
 * Remove all expired entries from IndexedDB.
 * Call this once on app load to keep the store lean.
 */
export async function idbPruneExpiredHtsMetadata() {
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (!cursor) return; // done — tx.oncomplete fires next
        if (Date.now() > cursor.value.expiresAt) cursor.delete();
        cursor.continue(); // must always advance, even after delete
      };
      req.onerror = () => {
        console.error('[htsIDBCache] Prune cursor error:', req.error);
      };
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch (e) {
    console.error('[htsIDBCache] idbPruneExpiredHtsMetadata failed:', e);
  }
}
